import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const CONSENT_KEY = "sds_cookie_consent";

export const getCookieConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY));
  } catch {
    return null;
  }
};

const saveConsent = (prefs) => {
  const consent = {
    necessary: true,
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
    ts: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent("sds-consent-updated", { detail: consent }));
  return consent;
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!getCookieConsent()) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const reopen = () => {
      const current = getCookieConsent();
      if (current) setPrefs({ analytics: !!current.analytics, marketing: !!current.marketing });
      setModalOpen(true);
    };
    window.addEventListener("sds-open-cookie-settings", reopen);
    return () => window.removeEventListener("sds-open-cookie-settings", reopen);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setVisible(false);
    setModalOpen(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setVisible(false);
    setModalOpen(false);
  };
  const savePrefs = () => {
    saveConsent(prefs);
    setVisible(false);
    setModalOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6"
            data-testid="cookie-banner"
            role="dialog"
            aria-label="Consenso cookie"
          >
            <div className="mx-auto max-w-4xl rounded-3xl border border-[#D8E5E8] bg-white/95 p-6 shadow-2xl shadow-[#10333B]/20 backdrop-blur-xl sm:p-7">
              <div className="flex flex-col gap-5 md:flex-row md:items-center">
                <div className="flex items-start gap-4 md:flex-1">
                  <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#1F8296]/40 bg-[#1F8296]/10 sm:flex">
                    <Cookie className="h-5 w-5 text-[#1F8296]" />
                  </div>
                  <p className="text-xs leading-relaxed text-[#42565D] sm:text-sm">
                    Questo sito utilizza cookie tecnici e — previo consenso — cookie analitici e di
                    profilazione, anche di terze parti, per migliorare la tua esperienza di navigazione.
                    Puoi accettare tutti i cookie, rifiutare quelli non necessari o personalizzare le tue
                    scelte. Consulta la{" "}
                    <Link to="/cookie-policy" data-testid="cookie-banner-policy-link" className="font-semibold text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">
                      Cookie Policy
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-col gap-2.5 sm:flex-row md:flex-col lg:flex-row">
                  <button
                    type="button"
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="rounded-full bg-[#2997AB] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#238496]"
                  >
                    Accetta tutti
                  </button>
                  <button
                    type="button"
                    data-testid="cookie-reject-all"
                    onClick={rejectAll}
                    className="rounded-full border border-[#C9DBDF] px-5 py-2.5 text-sm font-semibold text-[#33484F] transition-colors hover:border-[#1F8296]/60 hover:text-[#1F8296]"
                  >
                    Rifiuta
                  </button>
                  <button
                    type="button"
                    data-testid="cookie-customize"
                    onClick={() => setModalOpen(true)}
                    className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#5C7178] underline-offset-4 transition-colors hover:text-[#1F8296] hover:underline"
                  >
                    Personalizza
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          data-testid="cookie-preferences-modal"
          className="max-w-lg border-[#D8E5E8] bg-white text-[#1A2E33]"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1A2E33]">Preferenze cookie</DialogTitle>
            <DialogDescription className="text-sm text-[#5C7178]">
              Gestisci il consenso per ciascuna categoria di cookie. I cookie tecnici sono sempre attivi
              perché necessari al funzionamento del sito.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-2xl border border-[#D8E5E8] bg-[#F4F9FA] p-4">
              <div>
                <p className="text-sm font-semibold text-[#1A2E33]">Tecnici e necessari</p>
                <p className="mt-1 text-xs leading-relaxed text-[#5C7178]">
                  Indispensabili per navigazione, sicurezza, memorizzazione delle preferenze di consenso e
                  area riservata. Non richiedono consenso.
                </p>
              </div>
              <Switch checked disabled data-testid="cookie-toggle-necessary" aria-label="Cookie tecnici sempre attivi" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-2xl border border-[#D8E5E8] bg-[#F4F9FA] p-4">
              <div>
                <p className="text-sm font-semibold text-[#1A2E33]">Analitici</p>
                <p className="mt-1 text-xs leading-relaxed text-[#5C7178]">
                  Ci aiutano a capire come viene utilizzato il sito, in forma aggregata, per migliorare
                  contenuti e navigazione.
                </p>
              </div>
              <Switch
                checked={prefs.analytics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                data-testid="cookie-toggle-analytics"
                aria-label="Cookie analitici"
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-2xl border border-[#D8E5E8] bg-[#F4F9FA] p-4">
              <div>
                <p className="text-sm font-semibold text-[#1A2E33]">Marketing e profilazione</p>
                <p className="mt-1 text-xs leading-relaxed text-[#5C7178]">
                  Utilizzati per proporti contenuti e comunicazioni in linea con i tuoi interessi.
                </p>
              </div>
              <Switch
                checked={prefs.marketing}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                data-testid="cookie-toggle-marketing"
                aria-label="Cookie di marketing"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-end">
            <button
              type="button"
              data-testid="cookie-save-preferences"
              onClick={savePrefs}
              className="rounded-full bg-[#2997AB] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#238496]"
            >
              Salva preferenze
            </button>
            <button
              type="button"
              data-testid="cookie-modal-accept-all"
              onClick={acceptAll}
              className="rounded-full border border-[#C9DBDF] px-6 py-2.5 text-sm font-semibold text-[#33484F] transition-colors hover:border-[#1F8296]/60 hover:text-[#1F8296]"
            >
              Accetta tutti
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
